import React, { useEffect, useState, useMemo } from 'react';
import { parseLocalDate } from '../../lib/dateFmt';
import { Plus, Trash2, Loader2, Newspaper, ArrowUpDown, Save, X, Edit3, ArrowLeft, Eye } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { useReorder } from '../../lib/useReorder';
import ImageUploader from '../../components/ImageUploader';
import ImagePositionPicker from '../../components/ImagePositionPicker';
import RichTextEditor from '../../components/RichTextEditor';
import RichContentBlocksEditor from '../../components/RichContentBlocksEditor';
import ContentPreviewModal from '../../components/ContentPreviewModal';
import StatusSelector from '../../components/StatusSelector';
import StatusBadge from '../../components/StatusBadge';

const CATEGORIES = [
  { value: 'comunicado', label: 'Comunicado' },
  { value: 'actividad', label: 'Actividad' },
  { value: 'fit', label: 'FIT' },
  { value: 'evento', label: 'Evento' },
];

const EMPTY = { title: '', summary: '', content: '', content_blocks: [], category: 'comunicado', image_url: '', image_position: 'center', status: 'draft', published: false, is_private: false, audience: 'public', published_at: '' };

export default function AdminNewsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const dnd = useReorder(items);

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/news');
      setItems(data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  const visible = useMemo(() => {
    if (!dnd.reorderMode) return items;
    const byId = Object.fromEntries(items.map((n) => [n.id, n]));
    return dnd.orderDraft.map((id) => byId[id]).filter(Boolean);
  }, [items, dnd.reorderMode, dnd.orderDraft]);

  async function saveOrder() {
    await dnd.saveOrder(async (ids) => {
      await api.put('/admin/news/reorder', { ids });
      await refresh();
    });
  }

  function startCreate() { setEditingId(null); setForm(EMPTY); setShowForm(true); setError(''); }
  function startEdit(n) {
    setEditingId(n.id);
    setForm({
      title: n.title || '', summary: n.summary || '', content: n.content || '',
      content_blocks: Array.isArray(n.content_blocks) ? n.content_blocks : [],
      category: n.category || 'comunicado', image_url: n.image_url || '',
      image_position: n.image_position || 'center',
      // New: 3-state status (draft / private_link / public). Falls back to legacy fields.
      status: n.status || (n.published === false ? 'draft' : n.is_private ? 'private_link' : 'public'),
      preview_token: n.preview_token || '',
      published: n.published !== false, is_private: !!n.is_private,
      audience: n.audience || 'public',
      published_at: n.published_at ? n.published_at.slice(0, 10) : '',
    });
    setShowForm(true); setError('');
  }
  function cancelForm() { setShowForm(false); setEditingId(null); setForm(EMPTY); setError(''); }

  async function save() {
    if (!form.title.trim()) return;
    setSaving(true); setError('');
    try {
      if (editingId) await api.put(`/news/${editingId}`, form);
      else await api.post('/news', form);
      cancelForm(); await refresh();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    setSaving(false);
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar esta noticia?')) return;
    try { await api.delete(`/news/${id}`); await refresh(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  }

  if (showForm) {
    return (
      <div data-testid="admin-news-page" className="space-y-6 max-w-4xl">
        <div className="flex items-center gap-3">
          <button onClick={cancelForm} data-testid="admin-news-back-btn" className="p-2 rounded-lg text-charcoal-600 hover:bg-gray-100"><ArrowLeft size={18} /></button>
          <div>
            <h1 className="text-2xl font-bold text-charcoal-800">{editingId ? 'Editar noticia' : 'Nueva noticia'}</h1>
            <p className="text-charcoal-500 text-sm">{editingId ? 'Actualiza los datos y guarda los cambios.' : 'Publica una nueva noticia.'}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Título</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-news-title" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Categoría</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-news-category" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500">
                {CATEGORIES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Fecha de publicación</label>
            <input
              type="date"
              lang="es-MX"
              value={form.published_at || ''}
              onChange={(e) => setForm({ ...form, published_at: e.target.value })}
              data-testid="admin-news-date"
              className="w-full sm:w-60 rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
            />
            {form.published_at && (
              <p className="text-xs text-cmic-600 mt-1 font-medium" data-testid="admin-news-date-confirm">
                📅 {parseLocalDate(form.published_at).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })} ({parseLocalDate(form.published_at).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })})
              </p>
            )}
            <p className="text-[11px] text-charcoal-400 mt-1">Esta es la fecha que se muestra en la noticia/actividad. Si la dejas vacía, se usa la fecha de hoy.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Imagen destacada</label>
            <ImageUploader value={form.image_url || ''} onChange={(url) => setForm({ ...form, image_url: url })} testid="admin-news-image" shape="rect" />
            {form.image_url && (
              <div className="mt-3 p-3 rounded-xl border border-gray-100 bg-gray-50/60">
                <p className="text-xs font-medium text-charcoal-600 mb-2">Posición de la imagen en la tarjeta</p>
                <ImagePositionPicker
                  value={form.image_position || 'center'}
                  imageUrl={form.image_url}
                  onChange={(pos) => setForm({ ...form, image_position: pos })}
                  testid="admin-news-image-position"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Resumen breve</label>
            <textarea rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="admin-news-summary" placeholder="1–2 líneas que aparecerán en la tarjeta de la lista." className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 resize-y" />
          </div>

          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Contenido principal</label>
            <RichTextEditor
              value={form.content}
              onChange={(html) => setForm({ ...form, content: html })}
              placeholder="Escribe el contenido completo de la noticia…"
              testid="admin-news-content"
              minHeight={280}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Bloques adicionales (opcionales)</label>
            <RichContentBlocksEditor
              value={form.content_blocks}
              onChange={(blocks) => setForm({ ...form, content_blocks: blocks })}
            />
          </div>

          <StatusSelector
            value={form.status || 'draft'}
            onChange={(s) => setForm({ ...form, status: s })}
            previewToken={form.preview_token}
            previewPath="/es/noticias/preview"
          />

          <div className="p-4 rounded-xl border border-cmic-100 bg-cmic-50/30">
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Audiencia</label>
            <select value={form.audience || 'public'} onChange={(e) => setForm({ ...form, audience: e.target.value })}
              data-testid="admin-news-audience"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500">
              <option value="public">🌍 Público — visible para todos en /noticias</option>
              <option value="members">🔒 Solo miembros — solo en la Zona Interna</option>
            </select>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{error}</div>}

          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <button onClick={save} disabled={saving || !form.title.trim()} data-testid="admin-news-save-btn" className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-cmic-700 disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}{editingId ? 'Guardar cambios' : (form.status === 'public' ? 'Publicar' : 'Guardar')}
            </button>
            <button onClick={() => setShowPreview(true)} disabled={!form.title.trim()} data-testid="admin-news-preview-btn" className="inline-flex items-center gap-2 bg-white border border-cmic-200 text-cmic-700 rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-cmic-50 disabled:opacity-50">
              <Eye size={14} /> Vista previa
            </button>
            <button onClick={cancelForm} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          </div>
        </div>
        <ContentPreviewModal
          open={showPreview}
          onClose={() => setShowPreview(false)}
          title={form.title}
          subtitle={form.summary}
          imageUrl={form.image_url}
          contentHtml={form.content}
          contentBlocks={form.content_blocks}
          previewToken={form.preview_token}
          previewPath="/es/noticias/preview"
        />
      </div>
    );
  }

  return (
    <div data-testid="admin-news-page" className="space-y-6">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2"><Newspaper size={22} />Noticias</h1>
          <p className="text-charcoal-500 mt-1 text-sm">{items.length} noticias · Publica, edita o reordena.</p>
        </div>
        <div className="flex items-center gap-2">
          {!dnd.reorderMode ? (
            <>
              <button onClick={dnd.enterReorder} disabled={items.length < 2} data-testid="admin-news-reorder-btn" className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
                <ArrowUpDown size={14} />Reordenar
              </button>
              <button onClick={startCreate} data-testid="admin-news-create-btn" className="inline-flex items-center gap-2 bg-cmic-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-700">
                <Plus size={14} />Nueva noticia
              </button>
            </>
          ) : (
            <>
              <button onClick={dnd.cancelReorder} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"><X size={14} />Cancelar</button>
              <button onClick={saveOrder} disabled={dnd.saving} data-testid="admin-news-save-order-btn" className="inline-flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-600 disabled:opacity-60">
                {dnd.saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}Guardar orden
              </button>
            </>
          )}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-cmic-500" size={24} /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Newspaper size={36} className="text-charcoal-300 mx-auto mb-2" />
          <p className="text-charcoal-500 text-sm">Aún no hay noticias publicadas.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {visible.map((n) => (
            <li key={n.id} draggable={dnd.reorderMode} {...(dnd.reorderMode ? dnd.dragProps(n.id) : {})} data-testid={`admin-news-item-${n.id}`} className={`bg-white rounded-2xl border p-4 flex items-start gap-4 ${dnd.reorderMode ? 'cursor-move border-amber-200 bg-amber-50/50' : 'border-gray-200 hover:border-cmic-300 transition-all'}`}>
              {n.image_url && <img src={n.image_url} alt="" className="w-20 h-20 object-cover rounded-xl flex-shrink-0" style={{ objectPosition: n.image_position || 'center' }} />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="inline-block text-xs font-medium text-cmic-700 bg-cmic-50 px-2 py-0.5 rounded-full">{n.category}</span>
                  <StatusBadge status={n.status || (n.published === false ? 'draft' : n.is_private ? 'private_link' : 'public')} />
                  {n.audience === 'members' && (
                    <span className="text-[10px] font-semibold text-cmic-700 bg-cmic-50 border border-cmic-200 px-2 py-0.5 rounded-full">🔒 INTERNO</span>
                  )}
                  <span className="text-xs text-charcoal-400">{n.published_at?.slice(0, 10)}</span>
                </div>
                <p className="font-semibold text-charcoal-800 truncate">{n.title}</p>
                <p className="text-sm text-charcoal-500 line-clamp-2">{n.summary}</p>
              </div>
              {!dnd.reorderMode && (
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(n)} title="Editar" data-testid={`admin-news-edit-${n.id}`} className="p-2 rounded-lg text-charcoal-600 hover:bg-gray-100"><Edit3 size={14} /></button>
                  <button onClick={() => remove(n.id)} title="Eliminar" data-testid={`admin-news-delete-${n.id}`} className="p-2 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 size={14} /></button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
